import React, { useEffect, useState } from 'react';
import { useQuery } from '@apollo/client';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from '@mui/material';
import Load from '../../../components/Load';
import TournamentOrganizerItemView from '../../admin/organizer/components/TournamentOrganizerItemView';
import SelectUserOrganizerModal from '../../../components/liga/SelectUserOrganizerModal';
import { type OrganizadorFuncao } from '../../../validation/tournamentOrganizerSchema';
import {
  ORGANIZADORES_POR_TORNEIO,
  OrganizadoresPorTorneioData,
  OrganizadoresPorTorneioVars,
  criarOrganizador,
  removerOrganizador,
  Organizador,
} from '../../../services/organizadorService';

interface TournamentOrganizersTabProps {
  torneioId: string;
  canEditLiga: boolean;
  onNotify?: (message: string, severity: 'success' | 'error') => void;
}

interface OrganizerItem extends Omit<Organizador, 'id'> {
  id?: string;
  tempId: string;
}

const TournamentOrganizersTab: React.FC<TournamentOrganizersTabProps> = ({
  torneioId,
  canEditLiga,
  onNotify,
}) => {
  const {
    data: organizadoresData,
    loading: loadingOrganizadores,
    refetch,
  } = useQuery<OrganizadoresPorTorneioData, OrganizadoresPorTorneioVars>(
    ORGANIZADORES_POR_TORNEIO,
    {
      variables: { torneioId },
      skip: !torneioId,
    },
  );

  const [items, setItems] = useState<OrganizerItem[]>([]);
  const [deletingMap, setDeletingMap] = useState<Record<string, boolean>>({});
  const [saving, setSaving] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [deleteConfirmDialog, setDeleteConfirmDialog] = useState<{
    open: boolean;
    item: OrganizerItem | null;
  }>({ open: false, item: null });

  useEffect(() => {
    if (organizadoresData?.organizadoresPorTorneio) {
      const mapped = organizadoresData.organizadoresPorTorneio.map((o) => ({
        ...o,
        tempId: o.id,
      }));
      setItems(mapped);
    }
  }, [organizadoresData]);

  const handleAddClick = () => {
    setShowAddModal(true);
  };

  const handleAddOrganizer = async (
    usuarioId: string,
    nomeUsuario: string,
    nomeFuncao: OrganizadorFuncao,
  ) => {
    setSaving(true);
    try {
      const created = await criarOrganizador({
        torneioId: torneioId,
        usuarioId: usuarioId,
        nomeFuncao: nomeFuncao,
        nome: nomeUsuario, // Nome do usuário selecionado
        telefone: null,
        fotoUrl: null,
      });
      if (created) {
        await refetch();
        const message = 'Organizador adicionado com sucesso';
        if (onNotify) {
          onNotify(message, 'success');
        } else {
          alert(message);
        }
      }
    } catch (e: any) {
      const errorMessage =
        e?.message ||
        e?.response?.data?.message ||
        'Erro ao adicionar organizador';
      if (onNotify) {
        onNotify(errorMessage, 'error');
      } else {
        alert(errorMessage);
      }
    } finally {
      setSaving(false);
      setShowAddModal(false);
    }
  };

  const handleDelete = (item: OrganizerItem) => {
    // Usar a função canDeleteOrganizer para verificar se é único administrador
    if (!canDeleteOrganizer(item)) {
      const message =
        'Não é possível remover o único administrador do torneio. Deve haver pelo menos um organizador com função de Administrador.';
      if (onNotify) {
        onNotify(message, 'error');
      } else {
        alert(message);
      }
      return;
    }

    // Abrir dialog de confirmação
    setDeleteConfirmDialog({ open: true, item });
  };

  const handleCloseDeleteDialog = () => {
    setDeleteConfirmDialog({ open: false, item: null });
  };

  const handleConfirmDelete = async () => {
    const item = deleteConfirmDialog.item;
    if (!item || !item.id) {
      return;
    }

    setDeletingMap((prev) => ({ ...prev, [item.tempId]: true }));
    try {
      await removerOrganizador(item.id);
      await refetch();
      const message = 'Organizador removido com sucesso';
      if (onNotify) {
        onNotify(message, 'success');
      } else {
        alert(message);
      }
      handleCloseDeleteDialog();
    } catch (e: any) {
      const errorMessage =
        e?.message ||
        e?.response?.data?.message ||
        'Erro ao remover organizador';
      if (onNotify) {
        onNotify(errorMessage, 'error');
      } else {
        alert(errorMessage);
      }
    } finally {
      setDeletingMap((prev) => {
        const { [item.tempId]: _removed, ...rest } = prev;
        return rest;
      });
    }
  };

  const canDeleteOrganizer = (item: OrganizerItem): boolean => {
    // Verificar se é o único administrador
    const administradores = items.filter(
      (i) => i.nomeFuncao === 'ADMINISTRADOR' && i.id,
    );
    const isUnicoAdministrador =
      item.nomeFuncao === 'ADMINISTRADOR' &&
      administradores.length === 1 &&
      administradores[0].id === item.id;

    return !isUnicoAdministrador;
  };

  if (loadingOrganizadores) {
    return <Load />;
  }

  return (
    <div>
      <div className="d-flex justify-content-end mb-3">
        <button
          className="btn btn-secondary"
          onClick={handleAddClick}
          disabled={!canEditLiga || saving}
        >
          Novo organizador +
        </button>
      </div>

      <div className="row g-3">
        {items.map((item) => {
          const canDelete = canEditLiga && canDeleteOrganizer(item);
          return (
            <div key={item.tempId} className="col-12">
              <TournamentOrganizerItemView
                organizador={item}
                onDelete={canDelete ? () => void handleDelete(item) : undefined}
                busy={saving || !!deletingMap[item.tempId]}
                readOnly={!canEditLiga}
                disableDeleteReason={
                  !canDelete && item.nomeFuncao === 'ADMINISTRADOR'
                    ? 'Não é possível remover o único administrador do torneio'
                    : undefined
                }
              />
            </div>
          );
        })}
      </div>

      <SelectUserOrganizerModal
        open={showAddModal}
        onClose={() => setShowAddModal(false)}
        onSelect={handleAddOrganizer}
        existingOrganizerUserIds={items
          .filter((item) => item.usuario?.id)
          .map((item) => item.usuario!.id)}
      />

      {/* Dialog de Confirmação de Deleção */}
      <Dialog
        open={deleteConfirmDialog.open}
        onClose={
          deletingMap[deleteConfirmDialog.item?.tempId || '']
            ? undefined
            : handleCloseDeleteDialog
        }
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Remover Organizador</DialogTitle>
        <DialogContent dividers>
          <p>
            Tem certeza que deseja remover o organizador{' '}
            <strong>
              {deleteConfirmDialog.item?.usuario?.nome ||
                deleteConfirmDialog.item?.nome ||
                'este organizador'}
            </strong>
            ? Esta ação não pode ser desfeita.
          </p>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCloseDeleteDialog}
            disabled={!!deletingMap[deleteConfirmDialog.item?.tempId || '']}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleConfirmDelete}
            variant="contained"
            color="error"
            disabled={!!deletingMap[deleteConfirmDialog.item?.tempId || '']}
          >
            {deletingMap[deleteConfirmDialog.item?.tempId || '']
              ? 'Removendo...'
              : 'Remover'}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default TournamentOrganizersTab;

